// /live-uploading, English (source of truth). British English; written to the reader ("you"), plain and specific.
// Markup: «Label» = a button, screen or setting as CloudTether or Photohawk shows it (bold); `text` = monospace.
// Facts, as CloudTether's own screens show them (30 Oct 2026) and photohawk-serverless docs/cloudtether-api.md:
// connect with the Photohawk email + password; choose an EXISTING workspace → gallery → collection (CloudTether has
// no create-gallery/collection UI and no previews-first switch, so this page claims neither); several cameras on one
// shoot over USB and Wi-Fi; pocket mode; shot / sending / delivered counts; shoot settings «JPEG only — leave RAW files
// on the camera», «Only shots I rate in camera», «Also send videos» (Photohawk takes video on Max only); originals
// kept on the phone for 14 days after delivery; History; other destinations alongside Photohawk. Photohawk takes
// JPEG only; over the storage allowance with no card, new uploads pause.

const en = {
  seo: {
    title: "Upload live from your camera with CloudTether | Photohawk",
    description:
      "Shoot tethered with CloudTether and every JPEG goes straight into your Photohawk gallery as you shoot, ready for face and bib search and selling.",
  },
  hero: {
    badge: "With CloudTether",
    eyebrow: "On every Photohawk plan",
    title: "Upload live from your camera",
    subtitle:
      "Connect your camera to CloudTether on your phone and every JPEG you take goes straight into your Photohawk gallery. People can find themselves by face or bib number and buy while the event is still going.",
    primary: "Get CloudTether",
    secondary: "Set it up step by step",
    note: "CloudTether is a separate service with its own subscription. Your Photohawk plan doesn't change.",
  },
  steps: {
    heading: "How it works",
    subtitle: "Set it up once, then just shoot.",
    items: [
      {
        id: "camera",
        title: "Connect your camera",
        body: "Open CloudTether on your iPhone or Android phone and connect your camera by cable or Wi-Fi. You can add more than one.",
      },
      {
        id: "connect",
        title: "Connect Photohawk once",
        body: "In CloudTether, go to «Deliver», choose «Photohawk» and sign in with your Photohawk email and password.",
      },
      {
        id: "choose",
        title: "Choose the gallery",
        body: "Pick the workspace, gallery and collection the shoot delivers to, then tap «Use this gallery».",
      },
      {
        id: "shoot",
        title: "Shoot",
        body: "Each JPEG lands in that collection moments after you take it. Photohawk does the rest: search, watermarks and sales.",
      },
    ],
  },
  shots: {
    heading: "What it looks like",
    subtitle: "CloudTether on your phone, sending to Photohawk.",
    items: [
      {
        id: "connect",
        caption: "Connect Photohawk once, with your Photohawk email and password.",
        alt: "CloudTether's Connect Photohawk screen, with an email address, a hidden password and a Connect button",
      },
      {
        id: "choose-gallery",
        caption: "Choose the workspace, gallery and collection for the shoot.",
        alt: "CloudTether's Photohawk gallery screen with Workspace Northlight Event Photography, Gallery City Marathon 2026 and Collection Finish Line, and a Use this gallery button",
      },
      {
        id: "three-cameras",
        caption: "Tether several cameras to one shoot, by cable or Wi-Fi.",
        alt: "A live CloudTether shoot sending to Photohawk from three cameras, one by USB and two by Wi-Fi, with 21,437 shot, 4 sending and 21,433 delivered",
      },
      {
        id: "shoot-settings",
        caption: "Send JPEGs only, or just the shots you rate in camera.",
        alt: "CloudTether's Shoot settings: delivering to Photohawk's City Marathon 2026, Finish Line, with options for JPEG only, only shots rated in camera, and also sending videos",
      },
      {
        id: "delivered",
        caption: "See what's on the way and what's arrived in Photohawk.",
        alt: "A CloudTether shoot, City Marathon 2026, with 21,433 photos in Photohawk, 15 of 16 uploading and the latest photos on the way",
      },
      {
        id: "destinations",
        caption: "Send to Photohawk and your other services at the same time.",
        alt: "CloudTether's Deliver to screen with Photohawk and Dropbox connected and more services to add, such as Google Drive and OneDrive",
      },
    ],
    heroAlt: "A live CloudTether shoot sending to Photohawk: a runner at the finish line, with 21,437 shot, 4 sending and 21,433 delivered",
  },
  landing: {
    heading: "What happens when a photo lands",
    body: "Photohawk processes every photo on its own servers, so CloudTether only has to send it. Usually within a minute of arriving, each photo is:",
    points: [
      { id: "search", text: "searchable by face (selfie search), bib number and time" },
      { id: "protect", text: "shown with your watermark and preview protection, never as the original" },
      { id: "sell", text: "on sale with your gallery's products, and delivered to buyers automatically" },
    ],
  },
  shoot: {
    heading: "Made for a busy shoot",
    body: "CloudTether keeps sending while you keep shooting, and shows you where every photo is.",
    points: [
      { id: "cameras", text: "Tether several cameras to one shoot, over USB and Wi-Fi at once. Each camera's files are kept apart." },
      { id: "counts", text: "Live counts of what you've shot, what's sending and what's been delivered to Photohawk." },
      { id: "pocket", text: "«Pocket mode» darkens the screen while the tether keeps running, so you can put your phone away." },
      { id: "copies", text: "CloudTether keeps the originals on your phone for 14 days after delivery, and «History» lists every past shoot." },
      { id: "more", text: "Send to Photohawk and to other services, such as Dropbox or Google Drive, at the same time." },
    ],
  },
  files: {
    heading: "What gets uploaded",
    subtitle: "Photohawk takes JPEG photos. CloudTether's shoot settings decide what leaves the camera.",
    rows: [
      { id: "jpeg", label: "JPEG", value: "Uploaded, with its capture time and other details kept." },
      { id: "rawjpeg", label: "RAW + JPEG", value: "Turn on «JPEG only — leave RAW files on the camera» and only the JPEG is sent." },
      { id: "raw", label: "RAW only", value: "Not uploaded to Photohawk. Switch your camera to RAW + JPEG." },
      { id: "rated", label: "Rated shots", value: "Turn on «Only shots I rate in camera» to send just the shots you rate." },
      { id: "video", label: "Video", value: "With «Also send videos» on, clips go to Photohawk only on the Max plan." },
    ],
  },
  roles: {
    heading: "Who can do what",
    items: [
      { id: "galleries", text: "CloudTether lists the galleries and collections that already exist, so create them in Photohawk first." },
      { id: "upload", text: "Owners, administrators and team members with the photographer role can upload." },
      { id: "restricted", text: "If you're limited to certain galleries in a workspace, CloudTether shows only those." },
      { id: "sales", text: "Salespeople can't upload." },
    ],
  },
  trouble: {
    heading: "If something isn't right",
    items: [
      {
        id: "reconnect",
        title: "CloudTether asks you to reconnect",
        body: "Your Photohawk sign-in in CloudTether expires from time to time, for example after your password is reset. Tap «Reconnect Photohawk» and sign in again.",
      },
      {
        id: "missing",
        title: "Photos aren't appearing",
        body: "Check your camera is saving JPEG or RAW + JPEG, and that the shoot delivers to the collection you're looking at. A photo can take up to a minute to show.",
      },
      {
        id: "gallery",
        title: "Your gallery isn't listed",
        body: "Create the gallery and its collections in Photohawk first, then choose them again in CloudTether. Only owners and administrators can create galleries.",
      },
      {
        id: "paused",
        title: "Uploads have paused",
        body: "If your workspace is over its storage allowance with no card on file, new uploads pause until the owner adds a card. Nothing is deleted.",
      },
    ],
  },
  faq: {
    heading: "Questions",
    items: [
      {
        q: "Which Photohawk plans does it work on?",
        a: "Every plan, for photos. Videos go to Photohawk only on the Max plan.",
      },
      {
        q: "Does CloudTether keep my Photohawk password?",
        a: "No. You sign in once and CloudTether keeps a sign-in token, not your password. When the token expires, you sign in again.",
      },
      {
        q: "Can several of us shoot into the same gallery?",
        a: "Yes. Tether several cameras to one shoot in CloudTether, or each person connects their own Photohawk account and picks the same gallery, or a collection each.",
      },
      {
        q: "What if two photos have the same file name?",
        a: "A photo with the same name in the same collection replaces the earlier one. CloudTether keeps each camera's files apart, but if several people shoot into one collection, give each camera its own file name prefix.",
      },
      {
        q: "Do I still need the web uploader?",
        a: "For photos you didn't shoot tethered, yes. Folders from a card or a computer go up fastest with the web uploader.",
      },
    ],
  },
  cta: {
    heading: "Ready to shoot live?",
    body: "Get CloudTether, connect your Photohawk account once, and your next event fills your gallery as you shoot.",
    primary: "Get CloudTether",
    secondary: "Read the setup guide",
  },
};

export type LiveUploadingCopy = typeof en;
export default en;
